import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Table, Tooltip, message, Spin } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import Title from 'antd/es/typography/Title'
import type { Partner } from '@/data'
import { getPartners } from '@/api/lop/partners/getPartners'
import { deletePartner } from '@/api/lop/partners/deletePartner'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { resolveBackendSort } from '@/utils/backendSort'
import { SearchInput } from '@/components/SearchInput'
import { getErrorMessage } from '@/utils/getErrorMessage'

export const PartnersList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })
  const [sortField, setSortField] = useState('-created_at')
  const [search, setSearch] = useState('')

  const hasFetched = useRef(false)
  const canReadPartners = hasPermission(
    auth.user,
    'lop:ui:action:read:partners',
  )
  const canCreatePartners = hasPermission(
    auth.user,
    'lop:ui:action:create:partners',
  )
  const canUpdatePartners = hasPermission(
    auth.user,
    'lop:ui:action:update:partners',
  )
  const canDeletePartners = hasPermission(
    auth.user,
    'lop:ui:action:delete:partners',
  )

  const columnSortKeys = {
    name: 'name',
    affiliate_id: 'affiliate_id',
    created_at: 'created_at',
  } as const

  const fetchPartners = useCallback(
    async (page = 1, sort = sortField, query = search) => {
      try {
        setLoading(true)
        setError(null)
        const filters = query ? [{ key: 'partner', value: query }] : undefined
        const { data, meta } = await getPartners({ page, sort, filters })
        setPartners(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load partners')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [sortField, search],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchPartners(1, sortField, value)
    },
    [fetchPartners, sortField],
  )

  useEffect(() => {
    if (hasFetched.current) return
    if (!canReadPartners) return
    hasFetched.current = true
    fetchPartners()
  }, [canReadPartners, fetchPartners])

  const handleDelete = async (id: number) => {
    try {
      await deletePartner(String(id))
      message.success('Partner deleted')
      fetchPartners(pagination.current)
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete partner'))
      console.error(err)
    }
  }

  const columns: TableProps<Partner>['columns'] = [
    {
      title: 'Partner',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) =>
        canUpdatePartners ? (
          <Link to={`/partners/${record.id}/edit`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Affiliate ID',
      dataIndex: 'affiliate_id',
      key: 'affiliate_id',
      sorter: true,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string) =>
        value
          ? new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap="middle">
          {canUpdatePartners ? (
            <Tooltip title="Edit">
              <Link to={`/partners/${record.id}/edit`}>
                <EditOutlined style={{ fontSize: 16 }} />
              </Link>
            </Tooltip>
          ) : null}
          {canDeletePartners ? (
            <Tooltip title="Delete">
              <DeleteOutlined
                style={{ fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>Partners</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button onClick={() => fetchPartners()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Partners
        </Title>
        <Flex gap="small" align="center">
          <SearchInput
            onSearch={handleSearch}
            placeholder="Search by name or ID..."
          />
          {canCreatePartners ? (
            <Button type="primary" onClick={() => navigate('/partners/new')}>
              New
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {canReadPartners ? (
        <Spin spinning={loading}>
          <Table<Partner>
            rowKey="id"
            columns={columns}
            dataSource={partners}
            pagination={pagination}
            onChange={(p, _filters, sorter) => {
              const sort = resolveBackendSort(
                sorter,
                columnSortKeys,
                '-created_at',
              )
              setSortField(sort)
              fetchPartners(p.current, sort, search)
            }}
          />
        </Spin>
      ) : null}
    </div>
  )
}
